/**
 * Hooks section events into the Event Bus.
 */
document.addEventListener('DOMContentLoaded', () => {
  /**
   * Runs when the section is selected in the editor.
   */
  document.addEventListener('shopify:section:select', (event) => {
    theme.eventBus.emit('shopify:section:select', event);
  });

  /**
   * Runs when the section is de-selected in the editor.
   */
  document.addEventListener('shopify:section:deselect', (event) => {
    theme.eventBus.emit('shopify:section:deselect', event);
  });

  /**
   * Runs when the section is updated.
   */
  document.addEventListener('shopify:section:load', (event) => {
    theme.eventBus.emit('shopify:section:load', event);
  });

  /**
   * Keyboard events.
   */
  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') {
      theme.eventBus.emit('key:escape');
    }
  });

  /**
   * Cart events.
   */
  theme.eventBus.listen('cart:update', () => {
    console.log('cart:update');
    fetch(`${theme.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const elements = {
          count: document.querySelectorAll('[js-cart="count"]'),
          countContainer: document.querySelectorAll('[js-cart="countContainer"]'),
        };

        elements.count.forEach((element) => {
          element.textContent = cart.item_count;
        });

        elements.countContainer.forEach((element) => {
          if (cart.item_count >= 1) {
            element.classList.remove('visually-hidden');
            return;
          }

          element.classList.add('visually-hidden');
        });
      });
  });

  /**
   * Window overlay.
   */
  const windowOverlay = document.querySelector('[js-window-overlay]');

  if (windowOverlay) {
    theme.eventBus.listen('window-overlay-open', ({ offset } = {}) => {
      windowOverlay.classList.add('is-active');

      if (offset) {
        windowOverlay.style.setProperty('--offset', offset);
      } else {
        windowOverlay.style.removeProperty('--offset');
      }
    });

    theme.eventBus.listen('window-overlay-close', () => {
      windowOverlay.classList.remove('is-active');
    });

    windowOverlay.addEventListener('click', () => {
      theme.eventBus.emit('ToggleElement');
    });
  }
});

/**
 * A component which adds an item to cart.
 */
customElements.define(
  'cart-add-button',
  class CartAddButton extends HTMLElement {
    get button() {
      return this.querySelector('[js-cart-add-button="button"]') ?? this;
    }

    connectedCallback() {
      this.button.addEventListener('click', () => {
        this.button.classList.add('is-loading');

        fetch(`${theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: this.dataset.variantId,
                quantity: 1,
              },
            ],
          }),
        }).then(() => {
          theme.eventBus.emit('cart:update');
          theme.eventBus.emit('cart:added');

          this.button.classList.remove('is-loading');
        });
      });
    }
  }
);

/**
 * Provides useful functions for select elements.
 */
customElements.define(
  'select-provider',
  class SelectProvider extends HTMLElement {
    connectedCallback() {
      const selector = this.querySelector('select');

      if (!selector) {
        console.error('The `select-provider` element must contain a select element.');
        return;
      }

      if (this.dataset.default) {
        /**
         * Sets the default selected value.
         * - Useful for if you don't have access to the `option` elements in Liquid.
         */
        for (let i = 0, count = selector.options.length; i < count; i++) {
          const option = selector.options[i];

          if (this.dataset.default === option.value || this.dataset.default === option.innerHTML) {
            selector.selectedIndex = i;
          }
        }
      }
    }
  }
);

/**
 * Uses the Section Rendering API to replace itself based on an event.
 */
customElements.define(
  'dynamic-section',
  class DynamicSection extends HTMLElement {
    connectedCallback() {
      theme.eventBus.listen(this.dataset.event, () => this.handleEvent());

      if (this.hasAttribute('on-load')) {
        document.addEventListener('DOMContentLoaded', () => {
          this.handleEvent();
        });
      }
    }

    /**
     * Handles the trigger event.
     */
    async handleEvent() {
      const section = await this.fetchSection();
      const html = new DOMParser().parseFromString(section, 'text/html');
      const element = html.querySelector('dynamic-section');

      if (!element) {
        return;
      }

      this.replaceWith(element);
    }

    /**
     * Fetches the section with the Section Rendering API.
     */
    // fetchSection() {
    //   return fetch(`/?section_id=${this.dataset.sectionId}`).then((response) => response.text());
    // }
    fetchSection() {
      const pathnameParts = window.location.pathname.split("/");
      console.log(pathnameParts, "pathnameParts");
      const possibleLocale = pathnameParts[1];
      const supportedLocales = ["de", "fr", "it", "es", "de-de", "fr-fr"];
    
      // Use string, not RegExp, and fix template literal syntax
      const localePrefix = supportedLocales.includes(possibleLocale)
        ? `/${possibleLocale}`
        : "";
    
      const sectionUrl = `${localePrefix}/?section_id=${this.dataset.sectionId}`;
    
      return fetch(sectionUrl).then((response) => response.text());
    }
  }
);

/**
 * Replaces an element in the original container with a match from the source.
 *
 * @param {HTMLElement} original - The original container.
 * @param {HTMLElement} source - The incoming container.
 * @param {String} selector - The selector of the element to replace.
 * @param {Function} isEqual - A custom function for determining equality.
 * @returns {Boolean}
 */
function replaceElement(original, source, selector, isEqual) {
  if (selector) {
    original = original.querySelector(selector);
    source = source.querySelector(selector);
  }

  if (original && source) {
    if ((typeof isEqual === 'function' && isEqual(original, source)) || original.isEqualNode(source)) {
      return;
    }

    original.replaceWith(source);

    return true;
  }

  return false;
}

/**
 * Sets the dynamic sources for all dynamic images.
 * @param {HTMLElement} container - The container to find images.
 */
function setAllDynamicImages(container = document) {
  container.querySelectorAll('[js-dynamic-image]').forEach((element) => {
    setDynamicImage(element);
  });
}

/**
 * Sets the dynamic sources for the image element.
 * @param {HTMLImageElement} element - The image element.
 * @param {String} srcset - The srcset to replace.
 */
function setDynamicImage(element, defaultSrc) {
  const url = new URL(element.src);
  const currentWidth = Number(url.searchParams.get('width'));
  const maxWidth = Number(element.dataset.maxWidth);
  const width = element.clientWidth;
  const width2x = width * 2;

  if (currentWidth && width) {
    const src = (defaultSrc ?? element.src).replace(
      /width=\d+/,
      `width=${!isNaN(maxWidth) ? Math.min(width, maxWidth) : width}`
    );

    const src2x = (defaultSrc ?? element.src).replace(
      /width=\d+/,
      `width=${!isNaN(maxWidth) ? Math.min(width2x, maxWidth) : width2x}`
    );

    element.loading = 'eager';
    element.srcset = `${src} 1x, ${src2x} 2x`;
    element.src = src;
  }
}

/**
 * When the page loads, is updated or resized, set the dynamic images.
 */
setAllDynamicImages();

theme.eventBus.listen('shopify:section:load', ({ detail: { sectionId } }) => {
  const section = document.getElementById(`shopify-section-${sectionId}`);

  if (section) {
    setAllDynamicImages(section);
  }
});

const dynamicImageResizeObserver = new ResizeObserver(
  debounce((entries) => {
    for (let entry of entries) {
      setDynamicImage(entry.target);
    }
  })
);

document.querySelectorAll('[js-dynamic-image]').forEach((element) => {
  dynamicImageResizeObserver.observe(element);
});

/**
 * Disclosure toggle.
 * - Toggles the open state of the closest `details` element.
 */
customElements.define(
  'disclosure-toggle',
  class DisclosureToggle extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', () => {
        this.closest('details').toggleAttribute('open');
      });
    }
  }
);

/**
 * Toggles the truncated content based on if it's overflowing.
 */
customElements.define(
  'truncate-content',
  class TruncateContent extends HTMLElement {
    connectedCallback() {
      this.setNodeSelectors();
      this.setEventListeners();
      this.setOverflowState();
    }

    /**
     * Sets the node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        content: this.querySelector('[js-truncate-content="content"]'),
        button: this.querySelector('[js-truncate-content="button"]'),
      };
    }

    /**
     * Sets the event listeners.
     */
    setEventListeners() {
      this.resizeObserver = new ResizeObserver(
        debounce(() => {
          this.setOverflowState();
        })
      );

      this.resizeObserver.observe(this.nodeSelectors.content);

      if (this.nodeSelectors.button) {
        this.nodeSelectors.button.addEventListener('click', () => {
          this.classList.toggle('is-active');

          requestAnimationFrame(() => {
            this.setOverflowState();
          });
        });
      }
    }

    /**
     * Sets the overflow state.
     */
    setOverflowState() {
      const { scrollHeight, clientHeight } = this.nodeSelectors.content;

      if (this.classList.contains('is-active')) {
        return;
      }

      if (scrollHeight > clientHeight) {
        this.classList.add('is-overflowing');
        return;
      }

      this.classList.remove('is-overflowing');
    }
  }
);

/**
 * Video controller.
 */
customElements.define(
  'video-controller',
  class VideoController extends HTMLElement {
    constructor() {
      super();

      this.setNodeSelectors();

      if (!this.nodeSelectors.video) {
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            /**
             * Lazy loads video sources with `data-src`.
             */
            [...this.nodeSelectors.video.querySelectorAll('source[data-src]')].forEach((source) => {
              source.src = source.dataset.src;
            });

            this.nodeSelectors.video.load();
          }
        },
        {
          threshold: 0.25,
        }
      ).observe(this.nodeSelectors.video);

      this.setEventListeners();
    }

    /**
     * Sets the node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        mute: this.querySelector('[js-video-controller="mute"]'),
        video: this.querySelector('video'),
      };
    }

    /**
     * Sets event listeners.
     */
    setEventListeners() {
      if (this.nodeSelectors.mute) {
        this.nodeSelectors.mute.addEventListener('click', this.toggleMute.bind(this));
        this.nodeSelectors.video.addEventListener('volumechange', this.setIsMutedState.bind(this));
      }
    }

    /**
     * Toggles the video muted state.
     */
    toggleMute() {
      if (this.nodeSelectors.video.muted) {
        this.nodeSelectors.video.muted = false;
        return;
      }

      this.nodeSelectors.video.muted = true;
    }

    /**
     * Sets the "is muted" state on the video, used for styling.
     */
    setIsMutedState() {
      if (this.nodeSelectors.video.muted) {
        this.nodeSelectors.video.classList.add('is-muted');
        return;
      }

      this.nodeSelectors.video.classList.remove('is-muted');
    }
  }
);

/**
 * Applies the loop/centered slides fix to all carousels.
 */
function fixAllSwiperLoopCenteredSlides() {
  [...document.querySelectorAll('swiper-container')].forEach((container) => {
    container.addEventListener('swiperupdate', ({ detail: [swiper] }) => {
      swiperLoopCenteredSlidesFix(swiper);
      swiperLoopMinSlidesFix(swiper);
    });

    swiperLoopMinSlidesFix(container.swiper);
  });
}

fixAllSwiperLoopCenteredSlides();
